import { useEffect, useState } from "react";
import { IoMdRadioButtonOff, IoMdRadioButtonOn } from "react-icons/io";
import { IoCheckmarkCircleOutline, IoRadioButtonOff } from "react-icons/io5";
import { MdOndemandVideo } from "react-icons/md";
import IntendedLearners from "./IntendedLearners";
import CourseStructure from "./CourseStructure";
import SetupAndDemoApplication from "./SetupAndDemoApplication";
import CoursePricingApplication from "./CoursePricingApplication";
import MentorDetailsApplication from "./MentorDetailsApplication";
import MentorQualificationApplication from "./MentorQualificationApplication";
import { mentorApplicationFormValidation } from "./mentorApplicationFormValidation";
import Loader from "../Loader";
import { Hamburger, HamburgerIcon, MenuIcon, X } from "lucide-react";
import axios from "../../axios";
import { notifySuccess, notifyDanger } from "../../action/customFn";

const tabs = [
  {
    title: "Intended Learners",
    index: 0,
  },
  {
    title: "Course structure",
    index: 1,
  },
  {
    title: "Setup & demo video",
    index: 2,
  },
  {
    title: "Pricing",
    index: 3,
  },
  {
    title: "Contact details",
    index: 4,
  },
  {
    title: "Career & expertise",
    index: 5,
  },
];

export default function MentorApplicationStepFive({
  setApplicationState,
  applicationPiece,
  mentorApplicationPiece,
  applicationSubmit,
  loading,
  applicationId,
  setApplicationId,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [stepFiveComplete, setStepFiveComplete] = useState(false);
  const [pricingValid, setPricingValid] = useState(false);
  const [ilValid, setIlValid] = useState(false);
  const [setupValid, setSetupValid] = useState(false);
  const [csValid, setCsValid] = useState(false);
  const [mcValid, setMcValid] = useState(false);
  const [bgValid, setBgValid] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [savingSection, setSavingSection] = useState(null); // Track which section is being saved

  useEffect(() => {
    const validationResult = mentorApplicationFormValidation(
      applicationPiece,
      mentorApplicationPiece
    );
    setCsValid(
      validationResult.validContentDeliveryStatus &&
        validationResult.validCourseDuration &&
        validationResult.validCourseContentHours &&
        validationResult.validWeeklyDaySchedule
    );

    setIlValid(
      validationResult.validWhatWillStudentsLearn &&
        validationResult.validPrerequisites &&
        validationResult.validIntendedAudience
    );

    setSetupValid(
      validationResult.validCourseCommencementDate &&
        validationResult.validCourseBrochure &&
        validationResult.validCalendlyLink &&
        validationResult.validDemoLectureURL
    );

    setPricingValid(validationResult.validCourseFee);
    setMcValid(validationResult.validPersonalInformation);
    setBgValid(validationResult.validQualifications);
    setStepFiveComplete(validationResult.isComplete);
  }, [applicationPiece, mentorApplicationPiece]);

  // Save functions for each section
  const saveIntendedLearners = async () => {
    try {
      setSavingSection('intendedLearners');
      const formData = new FormData();
      formData.append('intendedLearners', JSON.stringify(applicationPiece.intendedLearners));
      
      // Include applicationId if it exists
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }
      
      const response = await axios.post('/register/mentor-application-partial', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store applicationId from response if provided
      if (response.data?.data?.applicationId && !applicationId) {
        setApplicationId(response.data.data.applicationId);
      }
      
      notifySuccess('Intended Learners section saved successfully');
    } catch (error) {
      notifyDanger('Failed to save Intended Learners section');
      console.log(error);
    } finally {
      setSavingSection(null);
    }
  };

  const saveCourseStructure = async () => {
    try {
      setSavingSection('courseStructure');
      const formData = new FormData();
      formData.append('contentDeliveryStatus', applicationPiece.contentDeliveryStatus);
      formData.append('courseDuration', applicationPiece.courseDuration.toString());
      formData.append('courseContentHours', applicationPiece.courseContentHours.toString());
      formData.append('weeklyDaySchedule', applicationPiece.weeklyDaySchedule);
      
      // Include applicationId if it exists
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }
      
      const response = await axios.post('/register/mentor-application-partial', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store applicationId from response if provided
      if (response.data?.data?.applicationId && !applicationId) {
        setApplicationId(response.data.data.applicationId);
      }
      
      notifySuccess('Course Structure section saved successfully');
    } catch (error) {
      notifyDanger('Failed to save Course Structure section');
      console.log(error);
    } finally {
      setSavingSection(null);
    }
  };

  const saveSetupAndDemo = async () => {
    try {
      setSavingSection('setupAndDemo');
      const formData = new FormData();
      
      if (applicationPiece.courseCommencementDate) {
        const dateValue = applicationPiece.courseCommencementDate instanceof Date 
          ? applicationPiece.courseCommencementDate.toISOString()
          : applicationPiece.courseCommencementDate;
        formData.append('courseCommencementDate', dateValue);
      }
      
      if (applicationPiece.calendlyLinkOptional) {
        formData.append('calendlyLinkOptional', applicationPiece.calendlyLinkOptional);
      }
      
      if (applicationPiece.courseBrochure && applicationPiece.courseBrochure instanceof File) {
        formData.append('courseBrochure', applicationPiece.courseBrochure);
      }
      
      if (applicationPiece.demoLectureURL) {
        formData.append('demoLectureURL', applicationPiece.demoLectureURL);
      }
      
      // Include applicationId if it exists
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }
      
      const response = await axios.post('/register/mentor-application-partial', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store applicationId from response if provided
      if (response.data?.data?.applicationId && !applicationId) {
        setApplicationId(response.data.data.applicationId);
      }
      
      notifySuccess('Setup & Demo section saved successfully');
    } catch (error) {
      notifyDanger('Failed to save Setup & Demo section');
      console.log(error);
    } finally {
      setSavingSection(null);
    }
  };

  const savePricing = async () => {
    try {
      setSavingSection('pricing');
      const formData = new FormData();
      formData.append('courseFee', JSON.stringify(applicationPiece.courseFee));
      
      // Include applicationId if it exists
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }
      
      const response = await axios.post('/register/mentor-application-partial', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store applicationId from response if provided
      if (response.data?.data?.applicationId && !applicationId) {
        setApplicationId(response.data.data.applicationId);
      }
      
      notifySuccess('Pricing section saved successfully');
    } catch (error) {
      notifyDanger('Failed to save Pricing section');
      console.log(error);
    } finally {
      setSavingSection(null);
    }
  };

  const saveContactDetails = async () => {
    try {
      setSavingSection('contactDetails');
      const formData = new FormData();
      formData.append('personalInformation', JSON.stringify(mentorApplicationPiece.personalInformation));
      
      // Include applicationId if it exists
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }
      
      const response = await axios.post('/register/mentor-application-partial', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store applicationId from response if provided
      if (response.data?.data?.applicationId && !applicationId) {
        setApplicationId(response.data.data.applicationId);
      }
      
      notifySuccess('Contact Details section saved successfully');
    } catch (error) {
      notifyDanger('Failed to save Contact Details section');
      console.log(error);
    } finally {
      setSavingSection(null);
    }
  };

  const saveCareerAndExpertise = async () => {
    try {
      setSavingSection('careerAndExpertise');
      const formData = new FormData();
      
      formData.append('educationalQualificationTitle', mentorApplicationPiece.qualifications.educationalQualification.title);
      
      if (mentorApplicationPiece.qualifications.educationalQualification.supportingDocument && 
          mentorApplicationPiece.qualifications.educationalQualification.supportingDocument instanceof File) {
        formData.append('supportingDocument', mentorApplicationPiece.qualifications.educationalQualification.supportingDocument);
      }
      
      formData.append('currentProfessionalRoleTitle', mentorApplicationPiece.qualifications.currentProfessionalRole.title);
      
      if (mentorApplicationPiece.qualifications.currentProfessionalRole.resume && 
          mentorApplicationPiece.qualifications.currentProfessionalRole.resume instanceof File) {
        formData.append('resume', mentorApplicationPiece.qualifications.currentProfessionalRole.resume);
      }
      
      formData.append('workExperience', mentorApplicationPiece.qualifications.workExperience.toString());
      formData.append('previouslyWorkedWithLMS', mentorApplicationPiece.qualifications.previouslyWorkedWithLMS.toString());
      
      // Include applicationId if it exists
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }
      
      const response = await axios.post('/register/mentor-application-partial', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store applicationId from response if provided
      if (response.data?.data?.applicationId && !applicationId) {
        setApplicationId(response.data.data.applicationId);
      }
      
      notifySuccess('Career & Expertise section saved successfully');
    } catch (error) {
      notifyDanger('Failed to save Career & Expertise section');
      console.log(error);
    } finally {
      setSavingSection(null);
    }
  };

  function getIcon(item) {
    if (item.title === "Intended Learners") {
      return ilValid ? (
        <IoCheckmarkCircleOutline size={24} color="green" />
      ) : (
        <IoRadioButtonOff size={24} color="gray" />
      );
    } else if (item.title === "Course structure") {
      return csValid ? (
        <IoCheckmarkCircleOutline size={24} color="green" />
      ) : (
        <IoRadioButtonOff size={24} color="gray" />
      );
    } else if (item.title === "Setup & demo video") {
      return setupValid ? (
        <IoCheckmarkCircleOutline size={24} color="green" />
      ) : (
        <IoRadioButtonOff size={24} color="gray" />
      );
    } else if (item.title === "Pricing") {
      return pricingValid ? (
        <IoCheckmarkCircleOutline size={24} color="green" />
      ) : (
        <IoRadioButtonOff size={24} color="gray" />
      );
    } else if (item.title === "Contact details") {
      return mcValid ? (
        <IoCheckmarkCircleOutline size={24} color="green" />
      ) : (
        <IoRadioButtonOff size={24} color="gray" />
      );
    } else if (item.title === "Career & expertise") {
      return bgValid ? (
        <IoCheckmarkCircleOutline size={24} color="green" />
      ) : (
        <IoRadioButtonOff size={24} color="gray" />
      );
    }
  }

  return (
    <div className="mentor-app-content" style={{ paddingBlock: "0px" }}>
      <div className="mentor-app-plan">
        <div className="mentor-app-sideplan">
          <p className="subhead">Plan your course</p>
          <ul className="course-plan45ul">
            {tabs.slice(0, 4).map((item) => (
              <li
                onClick={() => {
                  setActiveTab(item.index);
                }}
                style={{ borderColor: item.index === activeTab && "black" }}
              >
                <span>{getIcon(item)}</span>
                <span>{item.title}</span>
              </li>
            ))}
          </ul>
          <p className="subhead">Mentor Information</p>
          <ul className="course-plan45ul">
            {tabs.map(
              (item, index) =>
                index > 3 && (
                  <li
                    onClick={() => {
                      setActiveTab(item.index);
                    }}
                    style={{ borderColor: item.index === activeTab && "black" }}
                  >
                    <span>{getIcon(item)}</span>
                    <span>{item.title}</span>
                  </li>
                )
            )}
          </ul>
          <button
            onClick={applicationSubmit}
            className="step-five-btn"
            disabled={!stepFiveComplete}
          >
            {loading ? <Loader /> : "Submit for Review"}
          </button>
        </div>
        {window.screen.width < 576 && (
          <div
            className="d-flex align-items-center justify-content-between col-12 col-md-6"
            style={{ gridColumn: "span 2" }}
          >
            <MenuIcon
              onClick={() => setShowSidebar(!showSidebar)}
              className="mb-2 text-black"
            />

            <button
              onClick={() => {
                applicationSubmit();
                setShowSidebar(false);
              }}
              className="step-five-btn mb-2"
              disabled={!stepFiveComplete}
            >
              {loading ? <Loader /> : "Submit for Review"}
            </button>
          </div>
        )}
        {showSidebar && window.screen.width < 576 && (
          <div
            className="sidebar-modal col-12 col-md-6"
            style={{ gridColumn: "span 2" }}
          >
            <div className="sidebar-modal-content">
              <div className="sidebar-modal-header d-flex align-items-center justify-content-between">
                <h3 className="text-black">Course Navigation</h3>
                {/* <X
                  onClick={() => setShowSidebar(false)}
                  className="cursor-pointer text-black"
                /> */}
              </div>
              <div className="sidebar-modal-body">
                <ul className="course-plan45ul">
                  {tabs.slice(0, 4).map((item) => (
                    <li
                      key={item.index}
                      onClick={() => {
                        setActiveTab(item.index);
                        setShowSidebar(false);
                      }}
                      style={{
                        borderColor: item.index === activeTab && "black",
                      }}
                    >
                      <span>{getIcon(item)}</span>
                      <span>{item.title}</span>
                    </li>
                  ))}
                </ul>
                <p className="subhead">Mentor Information</p>
                <ul className="course-plan45ul">
                  {tabs.map(
                    (item, index) =>
                      index > 3 && (
                        <li
                          key={item.index}
                          onClick={() => {
                            setActiveTab(item.index);
                            setShowSidebar(false);
                          }}
                          style={{
                            borderColor: item.index === activeTab && "black",
                          }}
                        >
                          <span>{getIcon(item)}</span>
                          <span>{item.title}</span>
                        </li>
                      )
                  )}
                </ul>
                {/* <button
                  onClick={() => {
                    applicationSubmit();
                    setShowSidebar(false);
                  }}
                  className="step-five-btn mb-2"
                  disabled={!stepFiveComplete}
                >
                  {loading ? <Loader /> : "Submit for Review"}
                </button> */}
              </div>
            </div>
          </div>
        )}
        <div className="mentor-app-plancontent">
          <h2 className="head">{tabs[activeTab].title}</h2>
          <div className="mentor-plancontent-inner">
            {activeTab === 0 && (
              <>
                <IntendedLearners
                  setApplicationState={setApplicationState}
                  applicationPiece={applicationPiece}
                />
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={saveIntendedLearners}
                    className="step-five-btn"
                    disabled={savingSection === 'intendedLearners'}
                    style={{ minWidth: "150px" }}
                  >
                    {savingSection === 'intendedLearners' ? <Loader /> : "Save Section"}
                  </button>
                </div>
              </>
            )}
            {activeTab === 1 && (
              <>
                <CourseStructure
                  setApplicationState={setApplicationState}
                  applicationPiece={applicationPiece}
                />
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={saveCourseStructure}
                    className="step-five-btn"
                    disabled={savingSection === 'courseStructure'}
                    style={{ minWidth: "150px" }}
                  >
                    {savingSection === 'courseStructure' ? <Loader /> : "Save Section"}
                  </button>
                </div>
              </>
            )}
            {activeTab === 2 && (
              <>
                <SetupAndDemoApplication
                  setApplicationState={setApplicationState}
                  applicationPiece={applicationPiece}
                />
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={saveSetupAndDemo}
                    className="step-five-btn"
                    disabled={savingSection === 'setupAndDemo'}
                    style={{ minWidth: "150px" }}
                  >
                    {savingSection === 'setupAndDemo' ? <Loader /> : "Save Section"}
                  </button>
                </div>
              </>
            )}
            {activeTab === 3 && (
              <>
                <CoursePricingApplication
                  setApplicationState={setApplicationState}
                  applicationPiece={applicationPiece}
                />
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={savePricing}
                    className="step-five-btn"
                    disabled={savingSection === 'pricing'}
                    style={{ minWidth: "150px" }}
                  >
                    {savingSection === 'pricing' ? <Loader /> : "Save Section"}
                  </button>
                </div>
              </>
            )}
            {activeTab === 4 && (
              <>
                <MentorDetailsApplication
                  setApplicationState={setApplicationState}
                  applicationPiece={mentorApplicationPiece}
                />
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={saveContactDetails}
                    className="step-five-btn"
                    disabled={savingSection === 'contactDetails'}
                    style={{ minWidth: "150px" }}
                  >
                    {savingSection === 'contactDetails' ? <Loader /> : "Save Section"}
                  </button>
                </div>
              </>
            )}
            {activeTab === 5 && (
              <>
                <MentorQualificationApplication
                  setApplicationState={setApplicationState}
                  applicationPiece={mentorApplicationPiece}
                />
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={saveCareerAndExpertise}
                    className="step-five-btn"
                    disabled={savingSection === 'careerAndExpertise'}
                    style={{ minWidth: "150px" }}
                  >
                    {savingSection === 'careerAndExpertise' ? <Loader /> : "Save Section"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
